import { format, isValid, parseISO } from "date-fns";

import Axios from "./Axios";
import APIVariables, {
  buildPrescriptionAuditTrailPath,
} from "./api-variables";

const DEFAULT_PAGE_SIZE = 100;

/** Fixed "who/what" fields shown in the second part of the audit box, in order. */
export const AUDIT_ACTOR_FIELDS = [
  { key: "submitted_by", label: "Submitted by" },
  { key: "received_by", label: "Received by" },
  { key: "approved_by", label: "Approved by" },
  { key: "rejected_by", label: "Rejected by" },
  { key: "rejected_date", label: "Rejected date" },
  { key: "reverted_by", label: "Reverted by" },
  { key: "dispensed_by", label: "Dispensed by" },
];

/** Fixed prescription fields shown in the first part of the audit box, in order. */
export const AUDIT_SUMMARY_FIELDS = [
  { key: "prescription_id", label: "Prescription ID" },
  { key: "patient_id", label: "Patient ID" },
  { key: "molecule", label: "Molecule" },
  { key: "date_of_paf", label: "Date of PAF", isDate: true },
  { key: "indication", label: "Indication" },
  { key: "status", label: "Status" },
  { key: "date_of_dispensing", label: "Date of Dispensing", isDate: true },
];

/**
 * A raw value counts as empty when it is nullish or the literal string "None"
 * returned by the backend.
 */
export function isEmptyAuditValue(value) {
  if (value == null) return true;
  const text = String(value).trim();
  return text === "" || text === "None";
}

/**
 * Convert a change key to its display label, e.g.
 * `number_of_supply_weeks` -> "Number of supply weeks".
 */
export function humanizeChangeKey(key) {
  if (!key) return "";
  const spaced = String(key).replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Format an ISO date/datetime string for display, falling back to the raw text. */
export function formatAuditDate(value, dateFormat = "dd-MMM-yyyy") {
  if (isEmptyAuditValue(value)) return "—";
  try {
    const parsed = parseISO(String(value));
    if (isValid(parsed)) return format(parsed, dateFormat);
  } catch {
    // fall through to raw value
  }
  return String(value);
}

/** Format an audit change value tuple/scalar for display. */
export function formatChangeValue(value) {
  if (isEmptyAuditValue(value)) return "—";
  return String(value);
}

/**
 * Normalise the fixed part of an audit entry so the UI can render it without
 * repeating the "print only if not null" rules everywhere.
 */
export function getVisibleActorFields(entry) {
  if (!entry) return [];
  const rejected = !isEmptyAuditValue(entry.rejected_by);

  return AUDIT_ACTOR_FIELDS.filter(({ key }) => {
    // Always-print fields.
    if (key === "submitted_by" || key === "received_by") return true;
    // `dispensed_by` only prints when the prescription was not rejected.
    if (key === "dispensed_by") return !rejected;
    // Everything else prints only when it has a value.
    return !isEmptyAuditValue(entry[key]);
  }).map((field) => ({
    ...field,
    value: entry[field.key],
  }));
}

/** Flatten an `information_changed` entry's `changes` map into label/old/new rows. */
export function mapInformationChange(change) {
  if (!change || typeof change !== "object") {
    return { actor: "", timestamp: "", rows: [] };
  }
  const changes = change.changes ?? {};
  const rows = Object.keys(changes).map((key) => {
    const pair = Array.isArray(changes[key]) ? changes[key] : [null, changes[key]];
    return {
      key,
      label: humanizeChangeKey(key),
      from: pair[0],
      to: pair[1],
    };
  });
  return {
    actor: change.actor ?? "",
    timestamp: change.timestamp ?? "",
    rows,
  };
}

export function mapAuditTrailEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const informationChanged = Array.isArray(entry.information_changed)
    ? entry.information_changed
    : [];
  return {
    ...entry,
    information_changed: informationChanged.map(mapInformationChange),
  };
}

/**
 * List audit trail entries (server paginated).
 * @param {{ page?: number, pageSize?: number, signal?: AbortSignal }} params
 */
export async function fetchAuditTrail({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  signal,
} = {}) {
  const res = await Axios.get(APIVariables.PRESCRIPTIONS_AUDIT_TRAIL, {
    params: { page, page_size: pageSize },
    signal,
  });
  const data = res.data ?? {};
  const results = Array.isArray(data) ? data : data.results ?? [];
  return {
    count: Array.isArray(data) ? results.length : data.count ?? results.length,
    next: data.next ?? null,
    previous: data.previous ?? null,
    results: results.map(mapAuditTrailEntry).filter(Boolean),
  };
}

/** Retrieve the audit trail for a single prescription. */
export async function fetchPrescriptionAuditTrail(prescriptionId, { signal } = {}) {
  const res = await Axios.get(
    buildPrescriptionAuditTrailPath(prescriptionId),
    { signal }
  );
  return mapAuditTrailEntry(res.data);
}
